import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import StatusBadge from "../components/StatusBadge";
import ProjectMap from "../components/ProjectMap";
import ActiveInspectionCard from "../components/ActiveInspectionCard";
import ConsultingTeamPicker, { TEAM_ROLES } from "../components/ConsultingTeamPicker";
import {
  Plus, Layers, Send, ArrowLeft, FileSpreadsheet, FileText,
  AlertTriangle, Users as UsersIcon, Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { downloadUrl } from "../lib/api";
import DatePickerAZ from "../components/DatePickerAZ";

export default function ProjectDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [proj, setProj] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [curators, setCurators] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [segOpen, setSegOpen] = useState(false);
  const [segPick, setSegPick] = useState([]);
  const [segForm, setSegForm] = useState({
    contractor_id: "",
    start_km: "",
    end_km: "",
    deadline: "",
  });
  const [teamOpen, setTeamOpen] = useState(false);
  const [teamDraft, setTeamDraft] = useState({});
  const [teamMeta, setTeamMeta] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    const [p, c, ins, cur] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get("/users", { params: { role: "contractor" } }),
      api.get("/inspections", { params: { project_id: id } }),
      api.get("/users", { params: { role: "aayda_curator" } }).catch(() => ({ data: [] })),
    ]);
    setProj(p.data);
    setContractors(c.data);
    setInspections(ins.data);
    setCurators(cur.data || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canSegment = user?.role === "superadmin";
  // "Yeni Mərhələ" funksionallığı yalnız podratçı şirkət (və superadmin) tərəfində
  const canStage = ["contractor", "superadmin"].includes(user?.role);
  const isSuperadmin = user?.role === "superadmin";
  const teamComplete = proj?.team_complete !== false; // default true if undefined (older data)

  const openTeamDialog = async () => {
    try {
      const r = await api.get(`/projects/${id}/consulting-team`);
      setTeamMeta(r.data);
      const initial = {};
      TEAM_ROLES.forEach((role) => {
        const slot = r.data.team[role.key];
        if (slot?.user) initial[role.key] = slot.user.id;
      });
      setTeamDraft(initial);
      setTeamOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Komanda yüklənmədi");
    }
  };

  const submitTeam = async () => {
    try {
      await api.put(`/projects/${id}/consulting-team`, { team: teamDraft });
      toast.success("Komanda yeniləndi");
      setTeamOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  const openEditDialog = () => {
    setEditForm({
      name: proj.name,
      length_km: proj.length_km,
      description: proj.description || "",
      deadline: proj.deadline,
      curator_id: proj.curator_id || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    try {
      const payload = {
        name: editForm.name,
        length_km: Number(editForm.length_km),
        description: editForm.description,
        deadline: editForm.deadline,
      };
      // curator_id: null means "no change", "" means clear, otherwise set
      if (editForm.curator_id !== undefined && editForm.curator_id !== "") {
        payload.curator_id = editForm.curator_id;
      }
      await api.patch(`/projects/${id}`, payload);
      toast.success("Layihə yeniləndi");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  const deleteProject = async () => {
    if (!window.confirm(
      "Bu layihəni və ona aid bütün hissələri, mərhələləri və yoxlanışları " +
      "BİRDƏFƏLİK silmək istədiyinizə əminsinizmi?"
    )) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Layihə silindi");
      nav("/projects");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  const teamLockedRoles = teamMeta
    ? Object.entries(teamMeta.team)
        .filter(([_k, v]) => v?.has_active_tasks)
        .map(([k]) => k)
    : [];

  const createSegment = async () => {
    try {
      const payload = {
        ...segForm,
        project_id: id,
        start_km: Number(segForm.start_km),
        end_km: Number(segForm.end_km),
      };
      if (segPick.length === 2 &&
          segPick[0]?.lng != null && segPick[1]?.lng != null) {
        payload.start_lng = Number(segPick[0].lng);
        payload.start_lat = Number(segPick[0].lat);
        payload.end_lng = Number(segPick[1].lng);
        payload.end_lat = Number(segPick[1].lat);
      }
      await api.post("/segments", payload);
      toast.success("Hissə əlavə edildi");
      setSegOpen(false);
      setSegPick([]);
      setSegForm({ contractor_id: "", start_km: "", end_km: "", deadline: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  const requestInspection = async (stage_id) => {
    try {
      await api.post("/inspections", { stage_id, description: "" });
      toast.success("Yoxlanış sorğusu göndərildi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xəta");
    }
  };

  if (!proj)
    return <div className="mono text-sm">Yüklənir...</div>;

  return (
    <div className="space-y-6">
      <Link
        to="/projects"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
        data-testid="back-to-projects"
      >
        <ArrowLeft className="w-4 h-4" /> Layihələr
      </Link>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="label-overline mb-1">LAYİHƏ</div>
            <h1 className="font-heading font-black text-3xl tracking-tight">
              {proj.name}
            </h1>
            <p className="text-slate-600 mt-1">{proj.description}</p>
            <div className="flex gap-6 mt-4 mono text-sm">
              <span>
                <span className="text-slate-400">L:</span>{" "}
                <strong>{proj.length_km} km</strong>
              </span>
              <span>
                <span className="text-slate-400">{t("deadline")}:</span>{" "}
                <strong>{proj.deadline}</strong>
              </span>
              {proj.curator && (
                <span data-testid="project-curator-badge">
                  <span className="text-slate-400">Kurator:</span>{" "}
                  <strong>{proj.curator.full_name}</strong>
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {canStage && (
              <Button
                onClick={() => nav(`/projects/${id}/stage/new`)}
                className="bg-[#004D99] hover:bg-[#003366]"
                data-testid="new-stage-btn"
                disabled={!teamComplete}
                title={!teamComplete ? "Konsulting komandası tam deyil" : undefined}
              >
                <Plus className="w-4 h-4 mr-2" /> {t("new_stage")}
              </Button>
            )}
            {isSuperadmin && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openTeamDialog}
                  data-testid="manage-team-btn"
                >
                  <UsersIcon className="w-3.5 h-3.5 mr-1" /> Komanda
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditDialog}
                  data-testid="edit-project-btn"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Redaktə
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deleteProject}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="delete-project-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Sil
                </Button>
              </div>
            )}
          </div>
        </div>
        {!teamComplete && (
          <div
            className="mt-4 flex items-start gap-2 p-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm"
            data-testid="team-incomplete-banner"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold mb-0.5">
                Konsulting komandası tam deyil
              </div>
              <div className="text-xs">
                Bu layihə üçün 6 yoxlanış rolundan ən azı biri təyin edilməyib.
                Komanda tam olmadan podratçı mərhələ yarada bilməz və hissə təyin
                oluna bilməz.{" "}
                {isSuperadmin && (
                  <button
                    type="button"
                    onClick={openTeamDialog}
                    className="font-bold underline hover:text-amber-700"
                    data-testid="team-incomplete-cta"
                  >
                    Komandanı təyin et →
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-slate-100">
          <span className="label-overline text-slate-500 mr-2">Hesabat ixracı:</span>
          <a
            href={downloadUrl(`/projects/${id}/report.xlsx`)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs mono uppercase tracking-wide rounded-md border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 transition-colors"
            data-testid="project-export-xlsx"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel (.xlsx)
          </a>
          <a
            href={downloadUrl(`/projects/${id}/report.pdf`)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs mono uppercase tracking-wide rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50 transition-colors"
            data-testid="project-export-pdf"
          >
            <FileText className="w-3.5 h-3.5" /> PDF
          </a>
        </div>
      </div>

      {/* Map */}
      {(proj.start_lng != null || proj.segments.some((s) => s.start_lng != null)) && (
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-heading font-bold text-xl mb-4">Xəritə</h2>
          <ProjectMap
            projects={[proj]}
            segments={proj.segments}
            heatmap={[]}
            height={360}
          />
        </section>
      )}

      {/* Segments */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl">{t("segments")}</h2>
          {canSegment && (
            <Dialog open={segOpen} onOpenChange={setSegOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="new-segment-btn">
                  <Plus className="w-4 h-4 mr-2" /> {t("new_segment")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t("new_segment")}</DialogTitle>
                </DialogHeader>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <Label className="label-overline">Podratçı</Label>
                      <Select
                        value={segForm.contractor_id}
                        onValueChange={(v) =>
                          setSegForm({ ...segForm, contractor_id: v })
                        }
                      >
                        <SelectTrigger data-testid="seg-contractor">
                          <SelectValue placeholder="Seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {contractors.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.full_name} — {c.organization_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="label-overline">Başlanğıc (km)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={segForm.start_km}
                          onChange={(e) =>
                            setSegForm({ ...segForm, start_km: e.target.value })
                          }
                          data-testid="seg-start"
                        />
                      </div>
                      <div>
                        <Label className="label-overline">Son (km)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={segForm.end_km}
                          onChange={(e) =>
                            setSegForm({ ...segForm, end_km: e.target.value })
                          }
                          data-testid="seg-end"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="label-overline">{t("deadline")}</Label>
                      <DatePickerAZ
                        value={segForm.deadline}
                        onChange={(v) => setSegForm({ ...segForm, deadline: v })}
                        testid="seg-deadline"
                        minDate={new Date()}
                        maxDate={proj?.deadline ? new Date(proj.deadline) : undefined}
                      />
                    </div>
                    {segPick.length > 0 && (
                      <div
                        className="mono text-[10px] text-slate-500 bg-slate-50 rounded p-2"
                        data-testid="seg-pick-summary"
                      >
                        {segPick.map((p, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span
                              className={`w-4 h-4 rounded-full text-white text-[8px] flex items-center justify-center font-bold ${
                                i === 0 ? "bg-[#FF5722]" : "bg-[#004D99]"
                              }`}
                            >
                              {i === 0 ? "A" : "B"}
                            </span>
                            {p?.lng != null ? p.lng.toFixed(4) : "—"},{" "}
                            {p?.lat != null ? p.lat.toFixed(4) : "—"}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setSegPick([])}
                          className="text-[10px] mono uppercase text-red-600 hover:underline mt-1"
                          data-testid="seg-pick-reset"
                        >
                          Sıfırla
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="label-overline mb-2 block">
                      Hissənin xəritə üzərində yeri (A → B)
                    </Label>
                    <ProjectMap
                      selectable
                      onPick={setSegPick}
                      initialPick={segPick}
                      projects={[proj]}
                      segments={proj.segments}
                      heatmap={[]}
                      height={340}
                    />
                    <p className="text-[10px] mono text-slate-400 mt-2">
                      Xəritədən hissənin başlanğıc (A) və son (B) nöqtələrini klikləyin.
                      Bu məlumat AAYDA-ya layihənin hansı hissəsində kimin işlədiyini
                      vizual göstərir.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    onClick={createSegment}
                    className="bg-[#004D99] hover:bg-[#003366]"
                    data-testid="seg-submit"
                  >
                    {t("create")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
        {proj.segments.length === 0 ? (
          <div className="text-sm text-slate-500">{t("no_data")}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {proj.segments.map((s) => {
              const c = contractors.find((x) => x.id === s.contractor_id);
              return (
                <div
                  key={s.id}
                  className="border border-slate-200 rounded-lg p-4"
                  data-testid={`segment-${s.id}`}
                >
                  <div className="flex justify-between mb-2">
                    <div className="mono font-bold">
                      {s.start_km} — {s.end_km} km
                    </div>
                    <StatusBadge status={s.status === "in_progress" ? "active" : s.status} />
                  </div>
                  <div className="text-xs text-slate-500">
                    {c?.full_name || "—"} · {c?.organization_name}
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between mono text-xs mb-1">
                      <span>İcra</span>
                      <strong>{s.completion_pct}%</strong>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded">
                      <div
                        className="h-full bg-[#004D99] rounded"
                        style={{ width: `${s.completion_pct}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Stages */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-heading font-bold text-xl mb-4">{t("stages")}</h2>
        {proj.stages.length === 0 ? (
          <div className="text-sm text-slate-500">{t("no_data")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-3 py-2 label-overline">Tip</th>
                  <th className="px-3 py-2 label-overline">L × W × T</th>
                  <th className="px-3 py-2 label-overline">Sahə/Həcm/Tonaj</th>
                  <th className="px-3 py-2 label-overline">Dəyər</th>
                  <th className="px-3 py-2 label-overline">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {proj.stages.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`stage-row-${s.id}`}
                  >
                    <td className="px-3 py-3 font-semibold">
                      <Layers className="w-3 h-3 inline mr-1" />
                      {t(s.stage_type)}
                    </td>
                    <td className="px-3 py-3 mono text-xs">
                      {s.L} × {s.W} × {s.T ?? "—"}
                    </td>
                    <td className="px-3 py-3 mono text-xs">
                      {s.calc_result?.area_m2 && `${s.calc_result.area_m2} m²`}
                      {s.calc_result?.volume_m3 && ` · ${s.calc_result.volume_m3} m³`}
                      {s.calc_result?.tonnage_t && ` · ${s.calc_result.tonnage_t} t`}
                    </td>
                    <td className="px-3 py-3 mono font-bold">
                      {s.calc_result?.total_cost?.toLocaleString()} ₼
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={s.status === "pending_inspection" ? "pending" : s.status} />
                      {s.payment_authorized && (
                        <span
                          className="ml-2 inline-block text-[10px] mono uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded"
                          data-testid={`stage-paid-${s.id}`}
                          title="Ödəniş icazəsi verildi"
                        >
                          ÖDƏNİŞ
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {user?.role === "contractor" && s.status === "pending_inspection" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => requestInspection(s.id)}
                          data-testid={`request-insp-${s.id}`}
                        >
                          <Send className="w-3 h-3 mr-1" /> Yoxlanış
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Inspections — grouped by parent (sub-requests nested under their parent) */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl">{t("inspections")}</h2>
          {inspections.length > 0 && (
            <span className="text-xs mono text-slate-500">
              {inspections.filter(
                (i) => i.status === "active" || i.status === "awaiting_team_lead"
              ).length}{" "}
              aktiv / {inspections.length} cəmi
            </span>
          )}
        </div>
        {inspections.length === 0 ? (
          <div className="text-sm text-slate-500">{t("no_data")}</div>
        ) : (
          (() => {
            const parents = inspections.filter((i) => !i.parent_request_id);
            const subsByParent = {};
            for (const i of inspections) {
              if (i.parent_request_id) {
                (subsByParent[i.parent_request_id] =
                  subsByParent[i.parent_request_id] || []).push(i);
              }
            }
            // Orphan sub-requests (parent not in returned list)
            const parentIds = new Set(parents.map((p) => p.id));
            const orphans = inspections.filter(
              (i) => i.parent_request_id && !parentIds.has(i.parent_request_id),
            );
            const sortByDate = (a, b) =>
              new Date(b.created_at) - new Date(a.created_at);
            parents.sort(sortByDate);

            const renderActive = (i) => {
              const stage = proj.stages.find((s) => s.id === i.stage_id);
              return (
                <ActiveInspectionCard
                  key={i.id}
                  inspection={i}
                  stage={stage}
                />
              );
            };
            const renderCompact = (i, isSub = false) => {
              const stage = proj.stages.find((s) => s.id === i.stage_id);
              return (
                <Link
                  key={i.id}
                  to={`/inspections/${i.id}`}
                  className={`flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-md hover:bg-slate-50 hover:border-[#004D99] transition-colors ${
                    isSub ? "bg-amber-50/30" : ""
                  }`}
                  data-testid={
                    isSub ? `subrequest-${i.id}` : `insp-link-${i.id}`
                  }
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {isSub && (
                      <span className="text-amber-600 mono text-[10px] uppercase font-bold shrink-0">
                        ↳ Sub
                      </span>
                    )}
                    <div className="font-mono text-xs text-slate-500 shrink-0">
                      #{i.id.slice(0, 8)}
                    </div>
                    <div className="text-sm truncate flex-1">
                      {stage?.title || (stage ? t(stage.stage_type) : "—")}
                      {stage?.km_start != null && stage?.km_end != null && (
                        <span className="mono text-[10px] text-slate-400 ml-2">
                          {stage.km_start}-{stage.km_end} km
                        </span>
                      )}
                    </div>
                    <div className="mono text-[10px] text-slate-400 shrink-0">
                      {Math.min(i.current_step_index + 1, i.workflow.length)}/
                      {i.workflow.length}
                    </div>
                  </div>
                  <StatusBadge status={i.status} />
                </Link>
              );
            };

            return (
              <div className="space-y-4">
                {parents.map((p) => {
                  const isActive =
                    p.status === "active" || p.status === "awaiting_team_lead";
                  const subs = (subsByParent[p.id] || []).slice().sort(sortByDate);
                  return (
                    <div
                      key={p.id}
                      data-testid={`inspection-group-${p.id}`}
                      className="space-y-2"
                    >
                      {isActive ? (
                        renderActive(p)
                      ) : (
                        renderCompact(p, false)
                      )}
                      {subs.length > 0 && (
                        <div
                          className="ml-6 pl-4 border-l-2 border-amber-300 space-y-2"
                          data-testid={`subrequests-${p.id}`}
                        >
                          <div className="label-overline text-amber-700">
                            Sub-sorğular ({subs.length})
                          </div>
                          {subs.map((s) => renderCompact(s, true))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {orphans.length > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <div className="label-overline mb-2 text-slate-500">
                      Müstəqil sub-sorğular ({orphans.length})
                    </div>
                    <div className="space-y-2">
                      {orphans.map((s) => renderCompact(s, true))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}
      </section>

      {/* Consulting Team Management Dialog (superadmin only) */}
      <Dialog open={teamOpen} onOpenChange={setTeamOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Konsulting komandası</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 mb-2">
            Yoxlanış zəncirinin 6 mərhələsi üçün hər rolu təyin edin. Aktiv
            tapşırığı olan üzvlər kilidli görünəcək.
          </p>
          <ConsultingTeamPicker
            value={teamDraft}
            onChange={setTeamDraft}
            lockedRoles={teamLockedRoles}
            testidPrefix="team-edit"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTeamOpen(false)}
              data-testid="team-edit-cancel"
            >
              Ləğv et
            </Button>
            <Button
              onClick={submitTeam}
              className="bg-[#004D99] hover:bg-[#003366]"
              data-testid="team-edit-submit"
            >
              Yadda saxla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog (superadmin only) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Layihəni redaktə et</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-overline">Ad</Label>
              <Input
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-project-name"
              />
            </div>
            <div>
              <Label className="label-overline">Uzunluq (km)</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.length_km ?? ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, length_km: e.target.value })
                }
                data-testid="edit-project-length"
              />
            </div>
            <div>
              <Label className="label-overline">Son tarix</Label>
              <DatePickerAZ
                value={editForm.deadline || ""}
                onChange={(v) => setEditForm({ ...editForm, deadline: v })}
                testid="edit-project-deadline"
                minDate={new Date()}
              />
            </div>
            <div>
              <Label className="label-overline">Təsvir</Label>
              <Input
                value={editForm.description || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
                data-testid="edit-project-description"
              />
            </div>
            <div>
              <Label className="label-overline">AAYDA Kuratoru</Label>
              <Select
                value={editForm.curator_id || "__none"}
                onValueChange={(v) =>
                  setEditForm({ ...editForm, curator_id: v === "__none" ? "" : v })
                }
              >
                <SelectTrigger data-testid="edit-project-curator">
                  <SelectValue placeholder="Kurator təyin et" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Təyin edilməyib —</SelectItem>
                  {curators.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditOpen(false)}
              data-testid="edit-project-cancel"
            >
              Ləğv et
            </Button>
            <Button
              onClick={submitEdit}
              className="bg-[#004D99] hover:bg-[#003366]"
              data-testid="edit-project-submit"
            >
              Yadda saxla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
